import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import './ItemSpaceAssignmentPage.css';

// Phase ID for כללי items (whole-home items)
const KLALI_PHASE_ID = 'e7efa2ea-723d-44e2-9659-467a61b2fd38';

// localStorage key for dismissed items
const DISMISSED_STORAGE_KEY = 'admin_item_space_dismissed';

/**
 * ItemSpaceAssignmentPage - Admin tool for reviewing items not assigned to any space
 * and assigning them to one or more spaces via reference_suggested_items.
 * Shows items grouped by project phase, with checkboxes for each master_space.
 * Items stay visible after assigning spaces so you can check multiple.
 * Supports כללי assignment and dismissing irrelevant items.
 * Access via /admin/item-spaces (no sidebar link needed).
 */
const ItemSpaceAssignmentPage = () => {
  // Data state
  const [masterItems, setMasterItems] = useState([]);
  const [masterSpaces, setMasterSpaces] = useState([]);
  const [suggestedItems, setSuggestedItems] = useState([]); // reference_suggested_items
  const [projectPhases, setProjectPhases] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // UI state
  const [filter, setFilter] = useState('orphans'); // orphans, all, has-spaces, bundles-only, bundle-children, dismissed
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedPhases, setExpandedPhases] = useState(new Set());
  const [showDismissed, setShowDismissed] = useState(false);

  // Dismissed items (persisted in localStorage)
  const [dismissedIds, setDismissedIds] = useState(() => {
    try {
      const saved = localStorage.getItem(DISMISSED_STORAGE_KEY);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Track which items were originally orphans at load time
  // so they stay visible even after assigning spaces
  const [initialOrphanIds, setInitialOrphanIds] = useState(null);

  // ─── Data fetching ───────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, spacesRes, sugRes, phasesRes] = await Promise.all([
        supabase
          .from('master_items')
          .select(
            'id, item_name, is_bundle, bundle_type, bundle_items, include_in_boq, project_phase_id, item_options',
          )
          .order('item_name'),
        supabase.from('master_spaces').select('id, name').order('name'),
        supabase
          .from('reference_suggested_items')
          .select('id, master_space_id, master_item_id, is_suggested, display_order'),
        supabase.from('reference_project_phases').select('id, name'),
      ]);

      if (itemsRes.error) throw itemsRes.error;
      if (spacesRes.error) throw spacesRes.error;
      if (sugRes.error) throw sugRes.error;
      if (phasesRes.error) throw phasesRes.error;

      setMasterItems(itemsRes.data || []);
      setMasterSpaces(spacesRes.data || []);
      setSuggestedItems(sugRes.data || []);

      // Index phases by id
      const phaseMap = {};
      (phasesRes.data || []).forEach(p => {
        phaseMap[p.id] = p.name;
      });
      setProjectPhases(phaseMap);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Computed data ─────────────────────────────────────────────

  // Build a set of all bundle child IDs
  // Hide children of all bundles — space assignment happens at the parent bundle level
  const bundleChildIds = useMemo(() => {
    const ids = new Set();
    masterItems.forEach(item => {
      if (item.is_bundle && item.bundle_items?.items) {
        item.bundle_items.items.forEach(c => ids.add(c.item_id));
      }
    });
    return ids;
  }, [masterItems]);

  // Index suggested items: itemId → Set of spaceIds
  const suggestedByItem = useMemo(() => {
    const map = {};
    suggestedItems.forEach(si => {
      if (!map[si.master_item_id]) {
        map[si.master_item_id] = {};
      }
      map[si.master_item_id][si.master_space_id] = si;
    });
    return map;
  }, [suggestedItems]);

  // Enrich items with metadata — exclude bundle children entirely
  const enrichedItems = useMemo(() => {
    return masterItems
      .filter(item => !bundleChildIds.has(item.id))
      .map(item => {
        const spaceAssignments = suggestedByItem[item.id] || {};
        const assignedSpaceCount = Object.keys(spaceAssignments).length;
        const isBundleChild = bundleChildIds.has(item.id);
        const phaseName = projectPhases[item.project_phase_id] || 'ללא שלב';
        const isKlali = item.project_phase_id === KLALI_PHASE_ID;
        const isDismissed = dismissedIds.has(item.id);
        const isOrphan = !item.is_bundle && !isBundleChild && assignedSpaceCount === 0 && !isKlali;

        return {
          ...item,
          spaceAssignments,
          assignedSpaceCount,
          isBundleChild,
          phaseName,
          isKlali,
          isDismissed,
          isOrphan,
        };
      });
  }, [masterItems, suggestedByItem, bundleChildIds, projectPhases, dismissedIds]);

  // Capture initial orphan IDs on first data load so items stay visible
  // even after assigning spaces during this session
  useEffect(() => {
    if (!loading && initialOrphanIds === null && enrichedItems.length > 0) {
      const orphanIds = new Set(
        enrichedItems.filter(i => i.isOrphan && !i.isDismissed).map(i => i.id),
      );
      setInitialOrphanIds(orphanIds);
    }
  }, [loading, enrichedItems, initialOrphanIds]);

  // Stats
  const stats = useMemo(() => {
    const total = enrichedItems.length;
    const orphans = enrichedItems.filter(i => i.isOrphan && !i.isDismissed).length;
    const withSpaces = enrichedItems.filter(i => i.assignedSpaceCount > 0).length;
    const bundles = enrichedItems.filter(i => i.is_bundle).length;
    const children = enrichedItems.filter(i => i.isBundleChild).length;
    const dismissed = enrichedItems.filter(i => i.isDismissed).length;
    const klali = enrichedItems.filter(i => i.isKlali).length;
    return { total, orphans, withSpaces, bundles, children, dismissed, klali };
  }, [enrichedItems]);

  // Filter items
  const filteredItems = useMemo(() => {
    let items = enrichedItems;

    switch (filter) {
      case 'orphans':
        // Show items that were orphans at load time OR are still orphans,
        // excluding dismissed items
        items = items.filter(i => {
          if (i.isDismissed && !showDismissed) return false;
          return initialOrphanIds?.has(i.id) || i.isOrphan;
        });
        break;
      case 'has-spaces':
        items = items.filter(i => i.assignedSpaceCount > 0);
        break;
      case 'bundles-only':
        items = items.filter(i => i.is_bundle);
        break;
      case 'bundle-children':
        items = items.filter(i => i.isBundleChild);
        break;
      case 'dismissed':
        items = items.filter(i => i.isDismissed);
        break;
      default:
        break;
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(i => i.item_name.toLowerCase().includes(term));
    }

    return items;
  }, [enrichedItems, filter, searchTerm, initialOrphanIds, showDismissed]);

  // Group filtered items by phase
  const groupedByPhase = useMemo(() => {
    const groups = {};
    filteredItems.forEach(item => {
      const phase = item.phaseName;
      if (!groups[phase]) {
        groups[phase] = [];
      }
      groups[phase].push(item);
    });

    // Sort groups by phase name, put "ללא שלב" last
    return Object.entries(groups).sort((a, b) => {
      if (a[0] === 'ללא שלב') return 1;
      if (b[0] === 'ללא שלב') return -1;
      return a[0].localeCompare(b[0], 'he');
    });
  }, [filteredItems]);

  // ─── Actions ───────────────────────────────────────────────────

  // Persist dismissed IDs to localStorage
  const saveDismissed = useCallback(newSet => {
    setDismissedIds(newSet);
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...newSet]));
  }, []);

  // Toggle dismiss/undismiss an item
  const handleToggleDismiss = useCallback(
    itemId => {
      const next = new Set(dismissedIds);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      saveDismissed(next);
    },
    [dismissedIds, saveDismissed],
  );

  // Toggle כללי assignment (set/unset project_phase_id)
  const handleToggleKlali = async (itemId, currentlyKlali) => {
    setSaving(true);
    try {
      const newPhaseId = currentlyKlali ? null : KLALI_PHASE_ID;
      const { error } = await supabase
        .from('master_items')
        .update({ project_phase_id: newPhaseId })
        .eq('id', itemId);
      if (error) throw error;

      setMasterItems(prev =>
        prev.map(i => (i.id === itemId ? { ...i, project_phase_id: newPhaseId } : i)),
      );
    } catch (err) {
      console.error('Error toggling כללי:', err);
      alert('שגיאה בעדכון: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle space assignment for an item
  const handleToggleSpace = async (itemId, spaceId) => {
    const existing = suggestedByItem[itemId]?.[spaceId];
    setSaving(true);

    try {
      if (existing) {
        // Remove assignment
        const { error } = await supabase
          .from('reference_suggested_items')
          .delete()
          .eq('id', existing.id);
        if (error) throw error;

        setSuggestedItems(prev => prev.filter(si => si.id !== existing.id));
      } else {
        // Add assignment
        const { data, error } = await supabase
          .from('reference_suggested_items')
          .insert({
            master_item_id: itemId,
            master_space_id: spaceId,
            is_suggested: false,
            display_order: 0,
          })
          .select()
          .single();
        if (error) throw error;

        setSuggestedItems(prev => [...prev, data]);
      }
    } catch (err) {
      console.error('Error toggling space assignment:', err);
      alert('שגיאה בעדכון: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle phase group expand/collapse
  const togglePhase = phaseName => {
    setExpandedPhases(prev => {
      const next = new Set(prev);
      if (next.has(phaseName)) {
        next.delete(phaseName);
      } else {
        next.add(phaseName);
      }
      return next;
    });
  };

  // Expand all / collapse all
  const expandAll = () => {
    setExpandedPhases(new Set(groupedByPhase.map(([phase]) => phase)));
  };

  const collapseAll = () => {
    setExpandedPhases(new Set());
  };

  // Auto-expand all on first load when showing orphans
  useEffect(() => {
    if (!loading && groupedByPhase.length > 0 && expandedPhases.size === 0) {
      expandAll();
    }
  }, [loading]); // intentionally only re-run when loading changes

  // ─── Render ────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="item-space-page">
        <div className="item-space-loading">טוען נתונים...</div>
      </div>
    );
  }

  return (
    <div className="item-space-page">
      {/* Header */}
      <div className="item-space-header">
        <h1>שיוך פריטים לחללים</h1>
        <div className="item-space-header-actions">
          {saving && <span className="is-saving-indicator">שומר...</span>}
          <input
            type="text"
            className="item-space-search"
            placeholder="חיפוש פריט..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="item-space-stats">
        <div className="item-space-stat">
          סה&quot;כ פריטים: <strong>{stats.total}</strong>
        </div>
        <div className={`item-space-stat ${stats.orphans > 0 ? 'stat-orphan' : ''}`}>
          ללא חלל: <strong>{stats.orphans}</strong>
        </div>
        <div className="item-space-stat">
          עם חלל: <strong>{stats.withSpaces}</strong>
        </div>
        <div className="item-space-stat">
          כללי: <strong>{stats.klali}</strong>
        </div>
        <div className="item-space-stat">
          חבילות: <strong>{stats.bundles}</strong> | ילדי חבילות: <strong>{stats.children}</strong>
        </div>
        {stats.dismissed > 0 && (
          <div className="item-space-stat" style={{ background: '#f3f4f6' }}>
            לא רלוונטי: <strong>{stats.dismissed}</strong>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="item-space-filters">
        {[
          { key: 'orphans', label: `ללא חלל (${stats.orphans})` },
          { key: 'all', label: `הכל (${stats.total})` },
          { key: 'has-spaces', label: `עם חלל (${stats.withSpaces})` },
          { key: 'bundles-only', label: `חבילות (${stats.bundles})` },
          { key: 'bundle-children', label: `ילדי חבילות (${stats.children})` },
          ...(stats.dismissed > 0
            ? [{ key: 'dismissed', label: `לא רלוונטי (${stats.dismissed})` }]
            : []),
        ].map(f => (
          <button
            key={f.key}
            className={`is-filter-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
        {filter === 'orphans' && stats.dismissed > 0 && (
          <label className="is-filter-btn" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showDismissed}
              onChange={e => setShowDismissed(e.target.checked)}
              style={{ marginLeft: 4 }}
            />
            הצג לא רלוונטי
          </label>
        )}
        <div style={{ marginRight: 'auto', display: 'flex', gap: 6 }}>
          <button className="is-filter-btn" onClick={expandAll}>
            פתח הכל
          </button>
          <button className="is-filter-btn" onClick={collapseAll}>
            סגור הכל
          </button>
        </div>
      </div>

      {/* Phase groups */}
      {groupedByPhase.length === 0 ? (
        <div className="item-space-empty">אין פריטים להצגה</div>
      ) : (
        groupedByPhase.map(([phaseName, items]) => {
          const isExpanded = expandedPhases.has(phaseName);
          return (
            <div key={phaseName} className="phase-group">
              <div className="phase-group-header" onClick={() => togglePhase(phaseName)}>
                <span className={`phase-expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                <span className="phase-group-title">{phaseName}</span>
                <span className="phase-group-count">{items.length} פריטים</span>
              </div>
              {isExpanded && (
                <div className="item-space-table-container">
                  <table className="item-space-table">
                    <thead>
                      <tr>
                        <th style={{ width: '200px' }}>שם פריט</th>
                        <th style={{ width: '60px' }}>BOQ</th>
                        <th style={{ width: '80px' }}>סוג</th>
                        <th style={{ width: '60px' }}>כללי</th>
                        <th>חללים משויכים</th>
                        <th style={{ width: '50px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr
                          key={item.id}
                          className={`${item.isDismissed ? 'is-dismissed-row' : ''} ${item.assignedSpaceCount > 0 || item.isKlali ? 'is-assigned-row' : ''}`}
                        >
                          <td className="item-name-cell">
                            {item.item_name}
                            {item.isBundleChild && (
                              <span className="bundle-child-badge">ילד חבילה</span>
                            )}
                            {item.assignedSpaceCount > 0 && filter === 'orphans' && (
                              <span className="assigned-badge">✓ שויך</span>
                            )}
                          </td>
                          <td>
                            <span
                              className={`boq-badge ${item.include_in_boq ? 'boq-yes' : 'boq-no'}`}
                            >
                              {item.include_in_boq ? '✓' : '✗'}
                            </span>
                          </td>
                          <td>
                            {item.is_bundle ? (
                              <span className="item-name-cell">
                                <span
                                  className="item-type-tag"
                                  style={{ background: '#e0e7ff', color: '#3730a3' }}
                                >
                                  חבילה {item.bundle_type === 'choice' ? '(בחירה)' : ''}
                                </span>
                              </span>
                            ) : item.item_options ? (
                              <span className="item-name-cell">
                                <span className="item-type-tag">אפשרויות</span>
                              </span>
                            ) : (
                              <span style={{ color: '#9ca3af', fontSize: 12 }}>רגיל</span>
                            )}
                          </td>
                          <td>
                            <label className="klali-toggle">
                              <input
                                type="checkbox"
                                checked={item.isKlali}
                                onChange={() => handleToggleKlali(item.id, item.isKlali)}
                                disabled={saving}
                              />
                            </label>
                          </td>
                          <td>
                            <div className="space-checkboxes">
                              {masterSpaces.map(space => {
                                const isAssigned = !!item.spaceAssignments[space.id];
                                return (
                                  <label
                                    key={space.id}
                                    className={`space-checkbox-label ${isAssigned ? 'checked' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isAssigned}
                                      onChange={() => handleToggleSpace(item.id, space.id)}
                                      disabled={saving}
                                    />
                                    {space.name}
                                  </label>
                                );
                              })}
                            </div>
                          </td>
                          <td>
                            <button
                              className={`is-dismiss-btn ${item.isDismissed ? 'dismissed' : ''}`}
                              onClick={() => handleToggleDismiss(item.id)}
                              title={item.isDismissed ? 'החזר' : 'לא רלוונטי'}
                            >
                              {item.isDismissed ? '↩' : '✗'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default ItemSpaceAssignmentPage;
